import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Coins, CreditCard, Check, LogOut, Shield, Users, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth, apiFetch } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import MyTickets from "@/components/MyTickets";

const PLAN_ORDER = ["free", "starter", "builder", "studio", "enterprise"];

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [wallet, setWallet] = useState(null);
  const [plans, setPlans] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (!user) return;
    Promise.all([apiFetch("/wallet"), apiFetch("/plans", { auth: false }), apiFetch("/billing/history")])
      .then(([w, p, h]) => { setWallet(w); setPlans(p); setHistory(h); }).catch(() => {});
    // If returning from Stripe with a session_id, poll for fulfillment
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) {
      const poll = setInterval(async () => {
        try { const s = await apiFetch(`/payments/status/${sid}`, { auth: false });
          if (s.payment_status === "paid") { clearInterval(poll); toast.success("Payment successful!"); refresh(); apiFetch("/wallet").then(setWallet); window.history.replaceState(null, "", "/account"); }
        } catch {}
      }, 2000);
      setTimeout(() => clearInterval(poll), 30000);
    }
  }, [user, navigate, refresh]);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try { const { checkout_url } = await apiFetch("/payments/checkout", { method: "POST", body: { lookup_key, origin_url: window.location.origin } });
      window.location.href = checkout_url;
    } catch (e) { toast.error(e.message); setBusy(""); }
  };

  const cancelSub = async () => {
    setBusy("cancel");
    try { const r = await apiFetch("/subscription/cancel", { method: "POST" }); toast.success(r.message); }
    catch (e) { toast.error(e.message); } setBusy("");
  };

  const saveThresholds = async (thresholds) => {
    setBusy("th");
    try { const r = await apiFetch("/alerts/thresholds", { method: "PUT", body: { thresholds } });
      setWallet((w) => ({ ...w, usage: { ...w.usage, thresholds: r.thresholds } })); toast.success("Alert preferences saved"); }
    catch (e) { toast.error(e.message); } setBusy("");
  };

  if (!user || !wallet || !plans) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 right-0 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[150px] rounded-full" />
      <header className="relative z-10 max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3"><div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg">A</div><span className="font-display text-xl font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span></Link>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Link to="/teams" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-1.5" data-testid="account-teams-link"><Users size={13} /> {t("account.teams")}</Link>
          {user.role === "admin" && <Link to="/admin" className="font-mono text-xs uppercase tracking-widest text-[#00F0FF] inline-flex items-center gap-1.5" data-testid="account-admin-link"><Shield size={13} /> Admin</Link>}
          <button onClick={() => { logout(); navigate("/"); }} className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-1.5" data-testid="account-logout"><LogOut size={13} /> {t("account.logout")}</button>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 pb-24">
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Hi, {user.name}</h1>

        {wallet.usage?.low && (
          <div className="mt-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="account-low-credit-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} className="text-amber-400 shrink-0" />
              <p className="text-sm text-amber-200">You've used <b>{wallet.usage.percent_used}%</b> of your monthly credits ({wallet.credits.toLocaleString()} left). Top up to avoid interrupted builds.</p>
            </div>
            <button onClick={() => checkout("credits_25000")} disabled={busy} className="text-sm bg-amber-400 text-black font-semibold rounded-lg px-4 py-2 hover:bg-amber-300 transition-colors duration-300" data-testid="account-topup-btn">Buy 25k credits</button>
          </div>
        )}

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/10 p-6" data-testid="account-credits">
            <div className="flex items-center gap-2 text-[#00F0FF] mb-1"><Coins size={16} /><span className="font-mono text-[11px] uppercase tracking-widest">Credits</span></div>
            <p className="font-display font-extrabold text-4xl">{wallet.credits.toLocaleString()}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-1">Current plan</p>
            <p className="font-display font-bold text-2xl capitalize" data-testid="account-plan">{plans.plans[wallet.plan]?.name || wallet.plan}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-1">Buy credits</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => checkout("credits_5000")} disabled={busy} className="text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2 transition-colors duration-300" data-testid="buy-credits-5000">+5k · $15</button>
              <button onClick={() => checkout("credits_25000")} disabled={busy} className="text-xs bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2 transition-colors duration-300" data-testid="buy-credits-25000">+25k · $50</button>
            </div>
          </div>
        </div>

        {/* Plans */}
        <div className="flex items-center justify-between mt-12 mb-4">
          <h2 className="font-display font-bold text-xl">Subscription plans</h2>
          <div className="flex items-center gap-1 bg-white/5 rounded-full p-1">
            <button onClick={() => setAnnual(false)} className={`text-xs font-mono px-3 py-1.5 rounded-full ${!annual ? "bg-[#0055FF] text-white" : "text-slate-400"}`} data-testid="account-billing-monthly">Monthly</button>
            <button onClick={() => setAnnual(true)} className={`text-xs font-mono px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${annual ? "bg-[#0055FF] text-white" : "text-slate-400"}`} data-testid="account-billing-annual">Annual <span className="bg-[#00F0FF] text-black text-[9px] font-bold px-1.5 rounded-full">-20%</span></button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="account-plans">
          {PLAN_ORDER.filter((k) => k !== "enterprise").map((k) => {
            const p = plans.plans[k]; const current = wallet.plan === k;
            const price = annual ? p.price_annual : p.price;
            const lookup = annual ? p.lookup_annual : p.lookup;
            return (
              <div key={k} className={`rounded-2xl border p-5 ${current ? "border-[#00F0FF]" : "border-white/10"} bg-[#0a0a0a]`} data-testid={`account-plan-${k}`}>
                <p className="font-display font-bold text-lg">{p.name}</p>
                <p className="font-display font-extrabold text-3xl mt-2">${price || 0}<span className="text-slate-500 text-sm">{annual ? "/yr" : "/mo"}</span></p>
                <p className="text-[#00F0FF] text-sm mt-1">{p.credits.toLocaleString()} credits</p>
                <button
                  onClick={() => lookup && checkout(lookup)}
                  disabled={current || !lookup || busy === lookup}
                  className={`mt-4 w-full py-2.5 rounded-xl text-sm font-semibold transition-colors duration-300 ${current ? "bg-white/10 text-slate-400" : "bg-[#0055FF] text-white hover:bg-[#0047d6]"}`}
                  data-testid={`account-subscribe-${k}`}
                >
                  {busy === lookup ? <Loader2 size={14} className="animate-spin mx-auto" /> : current ? "Current plan" : k === "free" ? "Free" : "Upgrade"}
                </button>
              </div>
            );
          })}
        </div>
        {wallet.plan !== "free" && wallet.plan !== "enterprise" && (
          <button onClick={cancelSub} disabled={busy === "cancel"} className="mt-4 text-sm text-slate-500 hover:text-red-400 transition-colors duration-300" data-testid="account-cancel">Cancel subscription</button>
        )}

        {/* Usage alerts */}
        <h2 className="font-display font-bold text-xl mt-12 mb-3">Usage alerts</h2>
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="account-alerts">
          <p className="text-slate-400 text-sm mb-3">Email me when my monthly credit usage reaches:</p>
          <div className="flex flex-wrap gap-2">
            {[50, 75, 90, 100].map((t) => {
              const on = (wallet.usage?.thresholds || [75, 90, 100]).includes(t);
              return (
                <button key={t} onClick={() => saveThresholds(on ? (wallet.usage.thresholds || []).filter((x) => x !== t) : [...(wallet.usage?.thresholds || []), t])} disabled={busy === "th"} className={`text-sm rounded-lg px-4 py-2 transition-colors duration-300 ${on ? "bg-[#0055FF] text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`} data-testid={`account-threshold-${t}`}>{t}%</button>
              );
            })}
          </div>
        </div>

        {/* Usage ledger */}
        <h2 className="font-display font-bold text-xl mt-12 mb-4">Usage</h2>
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5 max-h-64 overflow-auto" data-testid="account-usage">
          {wallet.ledger.length === 0 && <p className="p-4 text-slate-500 text-sm">No activity yet.</p>}
          {wallet.ledger.map((l, i) => (
            <div key={i} className="flex items-center justify-between p-3 text-sm">
              <span className="text-slate-300 font-mono text-xs">{l.action}</span>
              <span className={l.amount < 0 ? "text-red-400" : "text-emerald-400"}>{l.amount > 0 ? "+" : ""}{l.amount}</span>
            </div>
          ))}
        </div>

        {/* Billing history */}
        <h2 className="font-display font-bold text-xl mt-12 mb-4">Billing history</h2>
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="account-billing">
          {history.length === 0 && <p className="p-4 text-slate-500 text-sm">No transactions yet.</p>}
          {history.map((t, i) => (
            <div key={i} className="flex items-center justify-between p-3 text-sm">
              <span className="text-slate-300">{t.lookup_key}</span>
              <span className="text-slate-500">${t.amount} · {t.payment_status}</span>
            </div>
          ))}
        </div>

        <MyTickets />
      </main>
    </div>
  );
}
